// NettoPositionBentoTile — Cash-Brücke 4 Wochen (Makeover 2026-07).
//
// Cash heute + erwartete Eingänge 4W − geplante Ausgänge 4W = Netto-4W.
// Beantwortet "reicht es?" in einer Zahl. Berechnung: lib/derive-actions.

import type { Netto4W } from "../../../lib/derive-actions";
import type { Severity } from "../../../lib/severity";
import { flashClass, useFlashOnChange } from "../../../hooks/useFlashOnChange";

const RESULT_COLOR: Record<Severity, string> = {
  critical: "text-pink-300",
  warn: "text-amber-200",
  ok: "text-emerald-300",
};

function fmt(v: number | null): string {
  if (v == null) return "—";
  return `${Math.round(v).toLocaleString("de-DE")} €`;
}

function Part({
  label,
  value,
  sign,
}: {
  label: string;
  value: number | null;
  sign?: "+" | "−";
}) {
  return (
    <div className="min-w-0">
      <p className="truncate text-[11px] uppercase tracking-wider text-slate-500">
        {sign ? `${sign} ${label}` : label}
      </p>
      <p className="truncate font-mono text-sm text-slate-200 sm:text-base">
        {fmt(value)}
      </p>
    </div>
  );
}

interface Props {
  netto: Netto4W;
  loading?: boolean;
}

export default function NettoPositionBentoTile({ netto, loading }: Props) {
  // K7-Kaskade: die Netto-Zahl pulst 1×, wenn sie sich nach Refetch/Edit
  // ändert — Teil der Flow-Welle durchs Cockpit.
  const nettoFlash = useFlashOnChange(netto.netto_eur);
  return (
    <div className="flex h-full flex-col justify-center">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Netto-Position 4 Wochen
        </h2>
        {loading && (
          <span className="h-3 w-16 animate-pulse rounded bg-slate-800/60" />
        )}
      </div>

      <div className="mt-2 grid grid-cols-2 items-center gap-3 sm:grid-cols-4">
        <Part label="Cash heute" value={netto.cash_eur} />
        <Part label="Eingänge 4W" value={netto.eingang_4w_eur} sign="+" />
        <Part label="Ausgänge 4W" value={netto.ausgang_4w_eur} sign="−" />
        <div className="min-w-0">
          <p className="truncate text-[11px] uppercase tracking-wider text-slate-500">
            = Netto 4W
          </p>
          <p
            className={`truncate font-mono text-base font-semibold sm:text-lg ${
              RESULT_COLOR[netto.severity]
            } ${flashClass(nettoFlash)}`}
          >
            {fmt(netto.netto_eur)}
          </p>
        </div>
      </div>

      {/* AUFTRAG J3 (2026-07-03): Die zwei Mini-Balken sind gestrichen —
          unbeschriftete Deko direkt unter den exakten Zahlen (Restraint:
          Farbe ist Information, nicht Schmuck). */}
      {netto.hinweis && (
        <p className="mt-1.5 truncate text-[11px] text-slate-500">{netto.hinweis}</p>
      )}
    </div>
  );
}
